import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Text,
} from "@fluentui/react-components";
import { Calculator24Regular, Dismiss24Filled, ShoppingBag24Regular } from "@fluentui/react-icons";
import React from "react";
import { useMsal } from "@azure/msal-react";
import { getPricingClient } from "../../services/dashboard.service";
import { processServerError } from "../../utils/helpers/error.helper";
import { GetPriceResponseModel, BuyNowPostModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { S365Button } from "../s365-button/s365-button.component";

type PricingCalculatorModalProps = {
    isOpened?: boolean;
    onClose?: () => void;
};

const DEFAULT_HOURS = 1;
const DEFAULT_ENGINEERS = 1;
const DEFAULT_MONTHS = 1;

const MAX_HOURS = 9999;
const MAX_ENGINEERS = 99;
const MAX_MONTHS = 24;

export const PricingCalculatorModal: React.FC<PricingCalculatorModalProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const { instance } = useMsal();

    const [hours, setHours] = React.useState<number>(DEFAULT_HOURS);
    const [engineers, setEngineers] = React.useState<number>(DEFAULT_ENGINEERS);
    const [months, setMonths] = React.useState<number>(DEFAULT_MONTHS);

    const [hoursInput, setHoursInput] = React.useState<string>(String(DEFAULT_HOURS));
    const [engineersInput, setEngineersInput] = React.useState<string>(String(DEFAULT_ENGINEERS));
    const [monthsInput, setMonthsInput] = React.useState<string>(String(DEFAULT_MONTHS));

    const [result, setResult] = React.useState<GetPriceResponseModel | undefined>(undefined);
    const [resultInputs, setResultInputs] = React.useState<{ hours: number; engineers: number; months: number } | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showRateLimitDialog, setShowRateLimitDialog] = React.useState<boolean>(false);

    React.useEffect(() => {
        setOpen(!!props.isOpened);
    }, [props.isOpened]);

    const onClose = () => {
        setOpen(false);
        if (props.onClose) {
            props.onClose();
        }
    };

    const getHoursError = (value: string): string | undefined => {
        const val = parseInt(value);
        if (value === "" || isNaN(val)) return "Please enter a number.";
        if (val < 1 || val > MAX_HOURS) return `Must be between 1 and ${MAX_HOURS}.`;
        return undefined;
    };

    const getEngineersError = (value: string): string | undefined => {
        const val = parseInt(value);
        if (value === "" || isNaN(val)) return "Please enter a number.";
        if (val < 1 || val > MAX_ENGINEERS) return `Must be between 1 and ${MAX_ENGINEERS}.`;
        return undefined;
    };

    const getMonthsError = (value: string): string | undefined => {
        const val = parseInt(value);
        if (value === "" || isNaN(val)) return "Please enter a number.";
        if (val < 1 || val > MAX_MONTHS) return `Must be between 1 and ${MAX_MONTHS}.`;
        return undefined;
    };

    const hoursError = getHoursError(hoursInput);
    const engineersError = getEngineersError(engineersInput);
    const monthsError = getMonthsError(monthsInput);
    const hasErrors = !!hoursError || !!engineersError || !!monthsError;

    const onHoursChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setHoursInput(ev.target.value);
        const val = parseInt(ev.target.value);
        if (!isNaN(val) && val >= 1 && val <= MAX_HOURS) {
            setHours(val);
        }
    };

    const onEngineersChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setEngineersInput(ev.target.value);
        const val = parseInt(ev.target.value);
        if (!isNaN(val) && val >= 1 && val <= MAX_ENGINEERS) {
            setEngineers(val);
        }
    };

    const onMonthsChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setMonthsInput(ev.target.value);
        const val = parseInt(ev.target.value);
        if (!isNaN(val) && val >= 1 && val <= MAX_MONTHS) {
            setMonths(val);
        }
    };

    const onCalculate = async () => {
        try {
            setIsLoading(true);
            const client = getPricingClient();
            const response = await client.getPrice(hours, engineers, months);
            setResultInputs({ hours, engineers, months });
            if (response.isRateLimited) {
                setResult(response);
                setShowRateLimitDialog(true);
            } else {
                setResult(response);
            }
        } catch (error) {
            processServerError(error, undefined, "An error occurred while calculating price.");
        } finally {
            setIsLoading(false);
        }
    };

    const [isBuyNowLoading, setIsBuyNowLoading] = React.useState<boolean>(false);

    const onBuyNow = async () => {
        try {
            setIsBuyNowLoading(true);
            const client = getPricingClient();
            const response = await client.buyNow(new BuyNowPostModel({ hours, engineers, months }));
            if (response?.url) {
                window.location.href = response.url;
            }
        } catch (error) {
            processServerError(error, undefined, "An error occurred while creating the checkout session.");
        } finally {
            setIsBuyNowLoading(false);
        }
    };

    const onRateLimitOk = async () => {
        setShowRateLimitDialog(false);
        try {
            await instance.logoutRedirect();
        } catch {
            // ignore logout errors
        }
    };

    // Derived values — use snapshotted inputs from when Calculate was pressed, not the live inputs
    const pricePerMonth = result && resultInputs ? result.yourPrice! / resultInputs.months : undefined;
    const pricePerEngineerMonth = result && resultInputs ? result.yourPrice! / resultInputs.engineers / resultInputs.months : undefined;
    const pricePerDay = pricePerMonth != null ? pricePerMonth * 12 / 365 : undefined;
    const pricePerEngineerDay = pricePerEngineerMonth != null ? pricePerEngineerMonth * 12 / 365 : undefined;
    const hoursPerMonth = result && resultInputs ? resultInputs.hours / resultInputs.months : undefined;
    const hoursPerEngineerMonth = result && resultInputs ? resultInputs.hours / resultInputs.engineers / resultInputs.months : undefined;

    const formatCurrency = (value: number) => value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const formatDecimal = (value: number) => value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    const formatPercent = (value: number) => (value * 100).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + "%";

    const resultRows: { label: string; value: string }[] = result
        ? [
              { label: "Your price", value: `€${formatCurrency(result.yourPrice!)}` },
              { label: "Price / hour", value: `€${formatCurrency(result.pricePerHour!)}` },
              { label: "Your discount", value: formatPercent(result.yourDiscount!) },
              { label: "Price / month", value: `€${formatCurrency(pricePerMonth!)}` },
              { label: "Price / engineer, month", value: `€${formatCurrency(pricePerEngineerMonth!)}` },
              { label: "Price / day", value: `€${formatCurrency(pricePerDay!)}` },
              { label: "Price / engineer, day", value: `€${formatCurrency(pricePerEngineerDay!)}` },
              { label: "Hours / month", value: formatDecimal(hoursPerMonth!) },
              { label: "Hours / engineer, month", value: formatDecimal(hoursPerEngineerMonth!) },
          ]
        : [];

    return (
        <>
            <Dialog
                modalType="alert"
                open={open}
                onOpenChange={(event, data) => {
                    setOpen(data.open);
                    if (!data.open) {
                        onClose();
                    }
                }}
            >
                <DialogSurface style={{ maxWidth: "520px" }}>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="transparent"
                                    onClick={showRateLimitDialog ? onRateLimitOk : onClose}
                                    icon={<Dismiss24Filled />}
                                />
                            }
                        >
                            <Calculator24Regular style={{ marginRight: "8px", verticalAlign: "middle" }} />
                            DWSIM Pro Pay-as-you-Go
                        </DialogTitle>
                        <DialogContent>
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "8px" }}>
                                {/* Inputs + Calculate button */}
                                <div style={{ display: "flex", gap: "8px", flexWrap: "nowrap", alignItems: "flex-start" }}>
                                    <Field
                                        label={`Hours (1–${MAX_HOURS})`}
                                        validationState={hoursError ? "error" : "none"}
                                        validationMessage={hoursError}
                                        style={{ width: "110px", flexShrink: 0 }}
                                    >
                                        <Input
                                            type="number"
                                            value={hoursInput}
                                            min={1}
                                            max={MAX_HOURS}
                                            onChange={onHoursChange}
                                            style={{ backgroundColor: "#fff9c4" }}
                                        />
                                    </Field>
                                    <Field
                                        label={`Engineers (1–${MAX_ENGINEERS})`}
                                        validationState={engineersError ? "error" : "none"}
                                        validationMessage={engineersError}
                                        style={{ width: "110px", flexShrink: 0 }}
                                    >
                                        <Input
                                            type="number"
                                            value={engineersInput}
                                            min={1}
                                            max={MAX_ENGINEERS}
                                            onChange={onEngineersChange}
                                            style={{ backgroundColor: "#fff9c4" }}
                                        />
                                    </Field>
                                    <Field
                                        label={`Months (1–${MAX_MONTHS})`}
                                        validationState={monthsError ? "error" : "none"}
                                        validationMessage={monthsError}
                                        style={{ width: "100px", flexShrink: 0 }}
                                    >
                                        <Input
                                            type="number"
                                            value={monthsInput}
                                            min={1}
                                            max={MAX_MONTHS}
                                            onChange={onMonthsChange}
                                            style={{ backgroundColor: "#fff9c4" }}
                                        />
                                    </Field>
                                    <div style={{ flexShrink: 0, paddingTop: "25px" }}>
                                        <S365Button
                                            appearance="primary"
                                            icon={isLoading ? <Spinner size="tiny" /> : <Calculator24Regular />}
                                            disabled={isLoading || hasErrors}
                                            onClick={onCalculate}
                                        >
                                            Calculate
                                        </S365Button>
                                    </div>
                                </div>

                                {/* Results */}
                                {result && !showRateLimitDialog && (
                                    <div>
                                        <Table size="small">
                                            <TableBody>
                                                {resultRows.map((row) => (
                                                    <TableRow key={row.label}>
                                                        <TableCell>
                                                            <Text weight={row.label === "Your price" || row.label === "Your discount" ? "semibold" : "regular"}>
                                                                {row.label}
                                                            </Text>
                                                        </TableCell>
                                                        <TableCell style={{ textAlign: "right", fontWeight: row.label === "Your price" || row.label === "Your discount" ? "600" : "normal" }}>
                                                            {row.value}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                        {/* Buy now button */}
                                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                                            <S365Button
                                                appearance="primary"
                                                icon={isBuyNowLoading ? <Spinner size="tiny" /> : <ShoppingBag24Regular />}
                                                disabled={isBuyNowLoading}
                                                onClick={onBuyNow}
                                            >
                                                Buy now
                                            </S365Button>
                                        </div>
                                    </div>
                                )}

                                {/* Rate limit warning */}
                                {showRateLimitDialog && (
                                    <MessageBar intent="warning">
                                        <MessageBarBody>
                                            <MessageBarTitle>Too many requests</MessageBarTitle>
                                            <br />
                                            You have exceeded the maximum number of pricing requests. Your session will be signed out.
                                        </MessageBarBody>
                                        <MessageBarActions>
                                            <Button
                                                appearance="primary"
                                                onClick={onRateLimitOk}
                                                style={{ marginRight: "8px" }}
                                            >
                                                OK
                                            </Button>
                                        </MessageBarActions>
                                    </MessageBar>
                                )}
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
